import { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Stack, TextField, Typography } from '@mui/material'
import _ from 'lodash'

import { useFormatCurrency } from '@/common/utils/formatting'
import { SpatialPolicy as Policy } from '@/models/spatialPolicy'
import { isEmpty } from '@/modules/map/utils'
import { useCurrentRegion } from '@/modules/urlRouting/hooks'

import { PolicyType } from '../../../utils/types'
import { EditableFieldProps } from '../../PolicyDetails/Form/fields/types'
import { FIELD_LABEL_SX } from '../styles'

import { valueFieldLabel } from './fieldOptions'
import { CurrencyField } from './inputs'
import { ThemedField, ValueText } from './ThemedField'

// The fields below show and hide in the same cases as the old fields in
// `PolicyDetails/Form/fields`. Each one is an input when editable, and text when not.

export const DescriptionField: FC<EditableFieldProps> = ({
  editable,
  editPolicy,
  onValidate,
  policy,
  ...props
}) => {
  const { t } = useTranslation()
  const label = t('policiesLibrary.formDescriptionTitle', 'Description')

  if (!editable)
    return (
      <ThemedField label={label} hidden={!policy.description}>
        {/* DO NOT TRANSLATE: DB Value */}
        <ValueText>{policy.description}</ValueText>
      </ThemedField>
    )

  return (
    <ThemedField label={label} {...props}>
      {id => (
        <TextField
          id={id}
          multiline
          minRows={3}
          size="small"
          fullWidth
          error={props.error}
          required={props.required}
          onBlur={onValidate}
          onChange={event => editPolicy?.(p => p.setDescription(event.target.value))}
          placeholder={t(
            'policiesLibrary.formDescriptionPlaceholder',
            'What do you want to do with this policy?'
          )}
          value={policy.description ?? ''}
        />
      )}
    </ThemedField>
  )
}

export const MessagesField: FC<EditableFieldProps> = ({
  editable,
  editPolicy,
  onValidate,
  policy,
  ...props
}) => {
  const { t } = useTranslation()
  const label = t('policiesLibrary.formMessageTitle', 'Message')
  const message: string | undefined = _.get(policy.messages, 'en-US')

  if (!editable)
    return (
      <ThemedField label={label} hidden={!message}>
        <ValueText>{message}</ValueText>
      </ThemedField>
    )

  return (
    <ThemedField label={label} {...props}>
      {id => (
        <TextField
          id={id}
          multiline
          minRows={3}
          size="small"
          fullWidth
          error={props.error}
          required={props.required}
          onBlur={onValidate}
          onChange={event => editPolicy?.(p => p.setMessages({ 'en-US': event.target.value }))}
          placeholder={t(
            'policiesLibrary.formMessagePlaceholder',
            'Leave an optional message for riders here...'
          )}
          value={message ?? ''}
        />
      )}
    </ThemedField>
  )
}

export const ValueField: FC<EditableFieldProps> = ({
  editable,
  editPolicy,
  onValidate,
  policy,
  ...props
}) => {
  const {
    data: { currencyCode },
  } = useCurrentRegion()
  const formatCurrency = useFormatCurrency(currencyCode)
  const label = valueFieldLabel(policy)

  if (!editable)
    return (
      <ThemedField
        label={label}
        hidden={
          (policy.policyType === PolicyType.PARKING_FEES &&
            !_.includes(policy.parkingFeeType, 'fixed')) ||
          !policy.value
        }
      >
        <ValueText>{formatCurrency(policy.value)}</ValueText>
      </ThemedField>
    )

  return (
    <ThemedField label={label} {...props}>
      {id => (
        <CurrencyField
          id={id}
          currencyCode={currencyCode}
          error={props.error}
          required={props.required}
          onBlur={onValidate}
          onChange={value => {
            const parsed = parseFloat(value)
            editPolicy?.(p => p.setValue(Number.isNaN(parsed) ? null : parsed))
          }}
          value={policy.value}
        />
      )}
    </ThemedField>
  )
}

/** Parse a whole number like the old Minimum and Maximum inputs. An empty field is `null`. */
const parseWholeNumber = (value: string): number | null => {
  if (isEmpty(value)) return null
  const parsed = Number.parseInt(value, 10)
  return Number.isNaN(parsed) ? null : parsed
}

interface NumberLimitFieldProps extends EditableFieldProps {
  label: string
  value: number | null | undefined
  onChange: (policy: Policy, value: number | null) => void
}

const NumberLimitField: FC<NumberLimitFieldProps> = ({
  label,
  value,
  onChange,
  editable,
  editPolicy,
  onValidate,
  ...props
}) => {
  const hasValue = !!value || value === 0

  if (!editable)
    return (
      <ThemedField label={label} hidden={!hasValue}>
        <ValueText>{value}</ValueText>
      </ThemedField>
    )

  return (
    <ThemedField label={label} {...props}>
      {id => (
        <TextField
          id={id}
          type="number"
          size="small"
          fullWidth
          error={props.error}
          required={props.required}
          onBlur={onValidate}
          onChange={event => {
            const parsed = parseWholeNumber(event.target.value)
            editPolicy?.(p => onChange(p, parsed))
          }}
          placeholder="00"
          value={hasValue ? value : ''}
        />
      )}
    </ThemedField>
  )
}

export const MinimumField: FC<EditableFieldProps> = props => {
  const { t } = useTranslation()
  return (
    <NumberLimitField
      {...props}
      label={t('policiesLibrary.formMinimumTitle', 'Minimum')}
      value={props.policy.minimum}
      onChange={(p, value) => p.setMinimum(value)}
    />
  )
}

export const MaximumField: FC<EditableFieldProps> = props => {
  const { t } = useTranslation()
  return (
    <NumberLimitField
      {...props}
      label={t('policiesLibrary.formMaximumTitle', 'Maximum')}
      value={props.policy.maximum}
      onChange={(p, value) => p.setMaximum(value)}
    />
  )
}

/**
 * Minimum, maximum and unit side by side, under the unit of the limits (for example "Vehicles
 * per Operator"). The same as the old `MinMaxWrapper`.
 */
export const MinMaxGroup: FC<{ policy: Policy; children: ReactNode }> = ({ policy, children }) => {
  const hasMin = !!policy.minimum || policy.minimum === 0
  const hasMax = !!policy.maximum || policy.maximum === 0

  return (
    <Stack spacing={1}>
      {policy.minMaxUnitLabel && (hasMin || hasMax) && (
        <Typography sx={{ ...FIELD_LABEL_SX, color: 'text.secondary' }}>
          {policy.minMaxUnitLabel}
        </Typography>
      )}
      <Stack
        direction="row"
        useFlexGap
        sx={{ flexWrap: 'wrap', gap: 2, '& > *': { flex: '1 1 140px', minWidth: 0 } }}
      >
        {children}
      </Stack>
    </Stack>
  )
}
